import React, { useState, useEffect, useImperativeHandle, useRef, useContext } from "react";
import { useLocation } from "react-router-dom";
import { Paper, Box } from "@material-ui/core";
import FiltersTable from "../../../components/FiltersTable";
import { generateFilterPayload, transformData } from "../../../components/FilterUtils";
import { commonStrings } from "../../../components/StringBundle";
import { getAuditReportsListApi } from "./Service";
import Snackbar from "../../../components/Snackbar";
import { exportExcelData } from "../../common";
import { useGlobalStyles } from "../../../components/globalStyles";
import { generateColumns } from "./constants";
import { auditReportStrings } from "../../../components/StringBundle";
import { AuthContext } from "../../../context/auth-context";

const AuditReportList = ({
  loadingExcelVal,
  exportRef,
  auditReportStatus,
  updateTotalRecords,
  totalrecord,
  activepage,
  setActivepage,
  fromDate,
  toDate,
  searchTrigger,
  company,
}) => {
  const globalClasses = useGlobalStyles();
  const location = useLocation();
  const auditReportString = auditReportStrings.forms;

  const savedPageSize = sessionStorage.getItem("auditReport.selectedpagesize");
  const savedFiltersRaw = sessionStorage.getItem(`FiltersTable.${location.pathname}`);

  const [allData, setAllData] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [pageSize, setPageSize] = useState(savedPageSize ? parseInt(savedPageSize) : 10);
  const [searchValue, setSearchValue] = useState(savedFiltersRaw ? JSON.parse(savedFiltersRaw) : []);
  const [showSnack, setSnack] = useState({});
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const isInitialRender = useRef(true);

  useImperativeHandle(exportRef, () => ({
    exportExcel,
  }));

  // useEffect for sessionStorage updates for related states
  useEffect(() => {
    sessionStorage.setItem("auditReport.selectedpagesize", pageSize);
    sessionStorage.setItem(`FiltersTable.${location.pathname}`, JSON.stringify(searchValue));
  }, [pageSize, searchValue]);

  useEffect(() => {
    if (!auditReportStatus) return;

    if (isInitialRender.current) {
      isInitialRender.current = false;

      callListApi(auditReportStatus, activepage, pageSize, searchValue, fromDate, toDate);
      return;
    }
    setSearchValue([]);
    setActivepage(1);

    callListApi(auditReportStatus, 1, pageSize, [], fromDate, toDate);
  }, [auditReportStatus, company]);

  useEffect(() => {
    if (!searchTrigger) return;

    callListApi(auditReportStatus, activepage, pageSize, searchValue, fromDate, toDate);
  }, [searchTrigger]);

  // payload formation
  function getSearchPayload(state, page, pagelimit, searchtext, from, to) {
    return transformData(
      generateFilterPayload(searchtext, {
        request_type: state,
        from_date: from || "",
        to_date: to || "",
        page: page,
        limit: pagelimit,
        company_name: company,
      })
    );
  }

  // list api call based on filter and status
  async function callListApi(
    state = auditReportStatus,
    page = activepage,
    pagelimit = pageSize,
    searchtext = searchValue,
    from = fromDate,
    to = toDate
  ) {
    setAllData(null);
    try {
      const searchPayload = getSearchPayload(state, page, pagelimit, searchtext, from, to);
      const response = await getAuditReportsListApi(searchPayload);
      const cols = generateColumns(response.header_details || []);
      setColumns(cols);
      if (response.body_details) {
        const dataWithKeys = response.body_details.map((item, idx) => ({
          ...item,
          key: idx,
        }));
        setAllData(dataWithKeys);
        updateTotalRecords(response?.total_count || dataWithKeys.length);
      } else {
        setAllData([]);
        updateTotalRecords(0);
      }
    } catch (e) {
      setHasError(true);
      setAllData([]);
      updateTotalRecords(0);
      setSnack({
        open: true,
        message: e.response?.data || commonStrings.serverError,
        severity: commonStrings.error,
      });
    }
  }

  // api call for export excel based on status and filter
  async function exportExcel() {
    try {
      setLoading(true);
      loadingExcelVal(true);
      const searchPayload = getSearchPayload(auditReportStatus, 1, totalrecord, searchValue, fromDate, toDate);
      const response = await getAuditReportsListApi(searchPayload);
      if (response.body_details) {
        const dataToExport = response.body_details.map(item => {
          const row = {};
          response.header_details.forEach(({ label, id }) => {
            row[label] = item[id] ?? "";
          });
          return row;
        });
        exportExcelData(dataToExport, auditReportString.auditReports);
      }
    } catch (e) {
      setSnack({
        open: true,
        message: e.response?.data || commonStrings.serverError,
        severity: commonStrings.error,
      });
    } finally {
      loadingExcelVal(false);
      setLoading(false);
    }
  }

  // pagination
  async function paginationChange(page, pagesize) {
    setActivepage(page);
    setPageSize(pagesize);
    sessionStorage.setItem("auditReport.selectedpagenumber", page);
    callListApi(auditReportStatus, page, pagesize, searchValue, fromDate, toDate);
  }

  // filter based on column search
  async function filterDataColumn(searchArr) {
    setSearchValue(searchArr === "" ? [] : searchArr);
    setActivepage(1);
    callListApi(auditReportStatus, 1, pageSize, searchArr === "" ? [] : searchArr, fromDate, toDate);
  }

  // Snack bar close function
  const snackbarClose = () => {
    setSnack({ open: false, message: "", severity: "" });
  };

  return (
    <Paper className={globalClasses.qliroot}>
      {showSnack.open && (<Snackbar {...showSnack} handleClose={snackbarClose} />)}
      {loading && 
        (
          <Box className="overlay_css">
            <p>{commonStrings.dataLoading}</p>
          </Box>
        )}
      <FiltersTable
        key={auditReportStatus}
        columns={columns}
        tableData={allData}
        onPaginationChange={paginationChange}
        totalrecord={totalrecord}
        filterDataColumn={filterDataColumn}
        apiFilter={true}
        hasError={hasError}
        pagination={activepage}
      />
    </Paper>
  );
};

export default AuditReportList;